import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import styles from "./styles";
import TextField from "../common/CustomTextField";
import constants from "../constants";
import Select from "../../../../../common/StyledSelect";
import Dialog from "./Dialog";
import { getProNumbers } from "../utils";
import { postData } from "../../../../../../api/componentAPIs";
import PanelAction from "./Action";
import Events from "../../../../../../utilities/EventEmitter";
import { sanitizeInput } from "../../../../../../utilities/dataUtils";

class ProNotes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      notes: "",
      notesCount: 0,
      selectedProNumber: null,
      proOptions: [],
      errorInfo: this.getDefaultErrorInfo(),
      isUpdateInProgress: false,
    };
  }

  componentDidMount() {
    this.generateProOptions();
  }

  getDefaultErrorInfo = () => {
    return {
      selectedProNumber: null,
      notes: null,
    };
  };

  generateProOptions = () => {
    const proNumbers = getProNumbers(this.props.stopDetails.shipments);
    this.setState({
      proOptions: proNumbers.map((proNumber) => ({
        label: proNumber,
        value: proNumber,
      })),
    });
  };

  updateNotes = (notes) => {
    this.setState((prevProps) => ({
      ...prevProps,
      notes,
      errorInfo: {
        ...prevProps.errorInfo,
        notes: this.getFieldError("notes", notes),
      },
      notesCount: notes.length,
    }));
  };

  getNotesByProNumber = (proId) => {
    return (
      this.props.stopDetails.shipments.find(
        (shipment) => shipment.proId?.toLowerCase() === proId?.toLowerCase()
      )?.notes ?? ""
    );
  };

  handleProSelection = (option) => {
    const selectedProNumber = option ? option.value : null;
    let preNotes = "";
    if (selectedProNumber) {
      preNotes = this.getNotesByProNumber(selectedProNumber);
    }
    this.setState((prevProps) => ({
      ...prevProps,
      selectedProNumber,
      notes: preNotes,
      errorInfo: {
        ...prevProps.errorInfo,
        selectedProNumber: this.getFieldError(
          "selectedProNumber",
          selectedProNumber
        ),
        notes: this.getFieldError("notes", preNotes),
      },
      notesCount: preNotes.length,
    }));
  };

  getFieldError = (field, value) => {
    switch (field) {
      case "selectedProNumber":
        return value ? null : `* Required`;
      case "notes":
        return value ? null : "* Required";
      default:
        return null;
    }
  };

  hasError = () => {
    let errorInfo = {};
    ["selectedProNumber", "notes"].forEach((fieldName) => {
      errorInfo[fieldName] = this.getFieldError(
        fieldName,
        this.state[fieldName]
      );
    });
    const hasError = Object.values(errorInfo).filter((value) => value).length;
    this.setState({
      errorInfo,
      ...(!hasError && { isUpdateInProgress: true }),
    });
    return hasError;
  };

  getShipmentIdOfSelectedPro = () => {
    return this.props.stopDetails.shipments.find(
      (shipment) =>
        shipment.proId?.toLowerCase() === this.state.selectedProNumber
    )?.shipmentId ?? null;
  };

  handleSubmit = () => {
    const { selectedProNumber, notes } = this.state;
    const requestParams = {
      shipmentId: this.getShipmentIdOfSelectedPro(),
      notes: sanitizeInput(notes),
      isPickupStop: this.props.stopDetails.isPickupStop,
      terminalCode: this.props.terminalCode,
    };
    if (!this.hasError()) {
      postData("UpdateProNotes", requestParams).then(
        (response) => {
          if (Math.trunc(response.status / 100) === 2) {
            this.props.enqueueSnackbar(
              `Notes added for pro number ${selectedProNumber}`,
              {
                variant: "success",
              }
            );
            Events.emit("UpdateDriverRoute", this.props.driverId);
            this.props.handleClose();
            return;
          }
          this.handleError(selectedProNumber, response.status);
        },
        () => {
          this.handleError(selectedProNumber);
        }
      );
    }
  };

  handleError = (selectedProNumber, statusCode) => {
    this.setState({isUpdateInProgress: false})
    if (statusCode === 403) {
      return;
    }
    this.props.enqueueSnackbar(`Error while adding note for pro number ${selectedProNumber}`, {
      variant: 'error'
    });
  };

  render() {
    const {
      selectedProNumber,
      proOptions,
      notes,
      notesCount,
      errorInfo,
      isUpdateInProgress,
    } = this.state;
    const { classes, handleClose, stopDetails } = this.props;
    return (
      <Dialog
        handleClose={handleClose}
        stopDetails={stopDetails}
      >
        <div className={classes.flexRow}>
          <Select
            textFieldProps={{
              id: "proNumber",
              label: "Select Pro",
              variant: "outlined",
              classes: {
                root: classes.inputLabel,
                shrink: classes.inputLabel,
              },
              helperText: this.state.errorInfo.selectedProNumber,
            }}
            InputPropsClasses={{
              input: classes.inputField,
            }}
            value={selectedProNumber}
            onChange={this.handleProSelection}
            error={Boolean(errorInfo.selectedProNumber)}
            data={proOptions}
            rootClass={classes.selectRoot}
          />
        </div>
        <span className={classes.notesLabel}>ADD NOTES TO PRO</span>
        <TextField
          id="proNotes"
          handleChange={this.updateNotes}
          value={notes}
          className={classes.fullWidth}
          multiline
          rows={6}
          error={errorInfo.notes}
          inputProps={{
            maxLength: constants.MAX_LENGTH_CONSTRAINTS.ADD_PRO_NOTES.COMMENT,
          }}
          InputPropsClasses= {{
            multiline: classes.innerPaddingField,
            input: classes.input,
          }}
          helperText={`${notesCount ?? 0}/${
            constants.MAX_LENGTH_CONSTRAINTS.ADD_PRO_NOTES.COMMENT
          }`}
        />
        <PanelAction
          handleClose={handleClose}
          closeButtonText="Close"
          confirmButtonText="Done"
          handleSubmit={this.handleSubmit}
          isLoading={isUpdateInProgress}
          isDisabled={isUpdateInProgress}
        />
      </Dialog>
    );
  }
}

export default compose(withStyles(styles), withSnackbar)(ProNotes);
